<template>
  <div class="control">
    <div class="text-input"
         :class="{ error, disabled }">
      <label :style="{ backgroundColor: labelBackground }">
        <span v-if="hint"
              class="help">
          <svg
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.00016 14.6667C11.6668 14.6667 14.6668 11.6667 14.6668 8.00004C14.6668 4.33337 11.6668 1.33337 8.00016 1.33337C4.3335 1.33337 1.3335 4.33337 1.3335 8.00004C1.3335 11.6667 4.3335 14.6667 8.00016 14.6667Z"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path d="M8 5.33337V8.66671"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
            <path
              d="M7.99609 10.6666H8.00208"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
            />
          </svg>

          <span>{{ hint }}</span>
        </span>
        <span class="text"> {{ label }} <span v-if="required"
                                              class="required">*</span></span>
      </label>
      <span v-if="prefix"
            class="prefix">{{ prefix }}</span>

      <div v-if="type == 'password'"
           class="show-icon"
           @click="togglePassword">
        <img
          v-if="showPassword"
          alt="show password"
          src="@/assets/images/routes/auth/show.svg"
          title="show password"
        />
        <img
          v-else
          alt="show password"
          src="@/assets/images/routes/auth/eye.svg"
          title="show password"
        />
      </div>

      <input
        v-if="
          type === 'input' ||
          type === 'time' ||
          type === 'text' ||
          type === 'email' ||
          type === 'range' ||
          type === 'number'
        "
        v-bind="$attrs"
        ref="inputFiled"
        autocomplete="off"
        :disabled="disabled"
        :placeholder="placeholder"
        :type="type === 'input' ? 'text' : type"
        :value="modelValue"
        @input="handleChange"
      />

      <input
        v-else-if="type == 'password'"
        ref="passwordInput"
        :disabled="disabled"
        :placeholder="placeholder"
        type="password"
        :value="modelValue"
        @input="handleChange"
      />

      <textarea
        v-else
        :disabled="disabled"
        :placeholder="placeholder"
        rows="4"
        type="text"
        :value="modelValue"
        @input="handleChange"
      />
      <span v-if="suffix"
            class="suffix">{{ suffix }}</span>
    </div>

    <p v-if="showCharacters"
       class="characters">
      maximum characters 50 ({{ modelValue.length }}/50)
    </p>
    <div v-if="error && $slots.errors"
         class="errors">
      <slot name="errors" />
    </div>

    <ul v-if="inputConditions"
        class="conditions-list">
      <li v-for="(condition, index) in inputConditions"
          :key="index"
          class="condition-item">
        {{ condition }}
      </li>
    </ul>
  </div>
</template>

<script>
  export default {
    name: 'TextInput',
    props: {
      label: {
        type: String,
        default: '',
      },
      labelBackground: {
        type: String,
        default: '#fff',
      },
      placeholder: {
        type: String,
        default: '',
      },
      hint: {
        type: String,
        default: '',
      },
      required: {
        type: Boolean,
        default: false,
      },
      modelValue: {
        type: [String, Number],
        default: '',
      },
      prefix: {
        type: String,
        default: '',
      },
      error: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
        default: 'input',
      },
      inputConditions: {
        type: Array,
        required: false,
      },
      showCharacters: {
        type: Boolean,
        default: false,
      },
      suffix: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        showPassword: false,
      };
    },
    methods: {
      handleChange(e) {
        this.$emit('update:modelValue', e.target.value);
      },
      togglePassword() {
        this.showPassword = !this.showPassword;
        if (this.$refs.passwordInput.type === 'password') {
          this.$refs.passwordInput.type = 'text';
        } else {
          this.$refs.passwordInput.type = 'password';
        }
      },
    },
    mounted() {
      setTimeout(() => {
        const input = this.$refs.passwordInput || this.$refs.inputFiled;
        if (
          input &&
          window.getComputedStyle(input).getPropertyValue('appearance') === 'menulist-button'
        ) {
          this.$emit('autoFilled');
        }
      }, 600);
    },
  };
</script>

<style lang="scss" scoped>
input[type='time']::-webkit-calendar-picker-indicator {
  background: url('../../../../assets/images/icons/time.svg');
  background-size: contain;
  cursor: pointer;
  height: 100%;
  aspect-ratio: 1/1;
  padding: 1rem;
  opacity: 1;
  border-radius: 12px;
}
.text-input {
  padding: 4px 16px;
  border-radius: 8px;
  border: 1px solid var(--gray500);
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;

  &:has(input) {
    height: 48px;
  }

  .prefix,
  .suffix {
    padding: 4px 8px;
    display: inline-block;
    color: var(--gray500);
    background: var(--gray50);
    border-radius: 4px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    font-size: 12px;
  }

  .show-icon {
    position: absolute;
    right: 17px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  input,
  textarea {
    width: 100%;
    font-size: 12px;
  }
  textarea {
    padding-top: 8px;
    min-height: 48px;
  }
  label {
    position: absolute;
    top: -11px;
    background: var(--shadesWhite);
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 0 4px;
    color: var(--gray500);
    .required {
      color: red;
    }
    .help {
      position: relative;
      cursor: pointer;

      svg {
        vertical-align: middle;
        path {
          stroke: var(--gray900);
        }
      }

      span {
        background: var(--shadesBlack);
        color: var(--shadesWhite);
        padding: 5px;
        border-radius: 8px;
        position: absolute;
        bottom: 100%;
        left: 0;
        width: 200px;
        display: none;
        z-index: 20;
      }
      &:hover {
        span {
          display: block;
        }
      }
    }
  }
  &.error {
    border-color: red;
    margin-bottom: 3px;
  }
  &.disabled {
    background: var(--gray50);
    label {
      background-color: var(--gray50) !important;
    }
    input {
      color: var(--color-gray-300);
    }
  }
}
.characters {
  font-size: 10px;
  font-style: normal;
  font-weight: 300;
  line-height: 100%;
  color: var(--gray400, #999);
  padding-inline-start: 12px;
  margin-block-start: 4px;
}
.errors {
  color: red;
}

.conditions-list {
  margin-top: 8px;
  list-style-type: circle;
  list-style-position: inside;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .condition-item {
    color: var(--gray500);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
  }
}

.v-locale-provider.v-locale--is-rtl {
  .show-icon {
    right: unset;
    left: 17px;
  }
}
</style>
